import { InputAdornment, type InputAdornmentProps } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { FaviconImage } from '~/components/FaviconImage';
import { useDebounce } from '~/hooks/useDebounce';
import { isEmail } from '~/scripts/isEmail';
import parseDomain from '~/scripts/parseDomain';

/** domain from an email, domain or URL, or undefined when invalid */
const getDomain = (value?: string): string => {
  if (!value) {
    return '';
  }
  if (value.includes('@') && !isEmail(value)) {
    return '';
  }
  try {
    return parseDomain(value.split('@').pop() || '');
  } catch {
    return '';
  }
};

interface FaviconDomainInputProps
  extends Omit<InputAdornmentProps, 'children' | 'position'> {
  /** email, domain or URL to show the favicon for, invalid shows the fallback */
  value?: string;
  /** shown while there is no valid (debounced) domain */
  fallback?: React.ReactNode;
}

/**
 * TextField adornment that shows the favicon of `value`'s domain (debounced),
 * or `fallback` when `value` is not a valid email, domain or URL.
 *
 * eg: `slotProps={{ input: { startAdornment: <FaviconDomainInput value={value} fallback={<Language />} /> } }}`
 */
export const FaviconDomainInput = ({
  value,
  fallback,
  ...inputAdornmentProps
}: FaviconDomainInputProps) => {
  const domain = useMemo(() => getDomain(value), [value]);
  const [debouncedDomain, , setDebouncedDomain] = useDebounce(domain || '');

  useEffect(() => {
    setDebouncedDomain(domain || '');
  }, [domain, setDebouncedDomain]);

  return (
    <InputAdornment position={'start'} {...inputAdornmentProps}>
      {domain && debouncedDomain ? (
        <FaviconImage domain={debouncedDomain} height={20} width={20} />
      ) : (
        fallback
      )}
    </InputAdornment>
  );
};
